import { baseApi } from '@core/api/baseApi';
import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  SearchUsersRequest,
  PaginatedResult,
  RolesResponse,
} from '../types/user.types';

export interface UserDetailsResponse {
  userId: string;
  roles: string[];
  permissions: string[];
}

export interface UserProfileResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  permissions: string[];
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * Users module API
 *
 * Provides user management endpoints:
 * - Current user's roles+permissions
 * - User CRUD operations
 * - User search with pagination
 * - Roles list
 */
export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get current user's roles and permissions
     * Backend endpoint: GET /api/users/roles/userDetails
     */
    getUserDetails: builder.query<UserDetailsResponse, void>({
      query: () => ({
        url: '/users/roles/userDetails',
        method: 'GET',
      }),
    }),

    /**
     * Search users with pagination and filters
     * Backend endpoint: POST /api/users/users/search
     */
    searchUsers: builder.mutation<PaginatedResult<User>, SearchUsersRequest>({
      query: (params) => ({
        url: '/users/users/search',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    /**
     * Create a new user
     * Backend endpoint: POST /api/users/users
     * Returns: Guid (user ID)
     */
    createUser: builder.mutation<string, CreateUserRequest>({
      query: (user) => ({
        url: '/users/users',
        method: 'POST',
        body: user,
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    /**
     * Update an existing user
     * Backend endpoint: PUT /api/users/users
     */
    updateUser: builder.mutation<void, UpdateUserRequest>({
      query: (user) => ({
        url: '/users/users',
        method: 'PUT',
        body: user,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    /**
     * Archive a user
     * Backend endpoint: PUT /api/users/users/{id}/archive
     */
    archiveUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/users/users/${id}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    /**
     * Restore an archived user
     * Backend endpoint: PUT /api/users/users/{id}/restore
     */
    restoreUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/users/users/${id}/restore`,
        method: 'PUT',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    /**
     * Get list of all available roles
     * Backend endpoint: GET /api/users/roles/list
     */
    getRoles: builder.query<RolesResponse, void>({
      query: () => ({
        url: '/users/roles/list',
        method: 'GET',
      }),
    }),

    /**
     * Get current user's profile
     * Backend endpoint: GET /api/users/users
     */
    getCurrentUserProfile: builder.query<UserProfileResponse, void>({
      query: () => ({
        url: '/users/users',
        method: 'GET',
      }),
      providesTags: [{ type: 'User', id: 'CURRENT' }],
    }),

    /**
     * Get a user by ID
     * Backend endpoint: GET /api/users/users/{id}
     */
    getUserById: builder.query<User, string>({
      query: (id) => ({
        url: `/users/users/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),

    /**
     * Change current user's password
     * Backend endpoint: POST /api/users/users/change-password
     */
    changePassword: builder.mutation<void, ChangePasswordRequest>({
      query: (data) => ({
        url: '/users/users/change-password',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetUserDetailsQuery,
  useSearchUsersMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useArchiveUserMutation,
  useRestoreUserMutation,
  useGetRolesQuery,
  useGetCurrentUserProfileQuery,
  useGetUserByIdQuery,
  useChangePasswordMutation,
} = usersApi;

